"use client";
import _extends from '@babel/runtime/helpers/extends';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { Box } from '../Box/Box.web.js';
import { getIsActive } from './dots.common.js';
import { IconButton } from '../IconButton/IconButton.web.js';
import { IconChevronRight } from '../icons/ui_navigation/IconChevronRight.web.js';
import { IconChevronLeft } from '../icons/ui_navigation/IconChevronLeft.web.js';
import { Dot } from './Dot.web.js';
import { visuallyHidden } from './styles.js';
import { useTheme } from '../utils/theming/useTheme.js';

var _excluded = ["count", "onChange", "showControls", "disabled", "carousel", "leftControlButtonProps", "rightControlButtonProps", "active", "size", "activeSize", "activeColor", "inactiveColor", "progression", "vertical", "dotsAriaLabel", "ariaLiveMessage"];
var Dots = function Dots(_ref) {
  var count = _ref.count,
    onChange = _ref.onChange,
    _ref$showControls = _ref.showControls,
    showControls = _ref$showControls === undefined ? false : _ref$showControls,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === undefined ? false : _ref$disabled,
    _ref$carousel = _ref.carousel,
    carousel = _ref$carousel === undefined ? false : _ref$carousel,
    leftControlButtonProps = _ref.leftControlButtonProps,
    rightControlButtonProps = _ref.rightControlButtonProps,
    _ref$active = _ref.active,
    active = _ref$active === undefined ? -1 : _ref$active,
    sizeProp = _ref.size,
    activeSizeProp = _ref.activeSize,
    activeColorProp = _ref.activeColor,
    inactiveColorProp = _ref.inactiveColor,
    _ref$progression = _ref.progression,
    progression = _ref$progression === undefined ? 'single' : _ref$progression,
    vertical = _ref.vertical,
    dotsAriaLabel = _ref.dotsAriaLabel,
    ariaLiveMessage = _ref.ariaLiveMessage,
    props = _objectWithoutProperties(_ref, _excluded);
  var _React$useState = React.useState(''),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    ariaLiveAnnouncement = _React$useState2[0],
    setAriaLiveAnnouncement = _React$useState2[1];
  var theme = useTheme();
  var dotsTheme = theme.dots.web;
  var size = React.useMemo(function () {
    return sizeProp || theme.dots.web.size.inactive;
  }, [sizeProp, theme.dots.web.size.inactive]);
  var activeSize = React.useMemo(function () {
    return activeSizeProp || theme.dots.web.size.active;
  }, [activeSizeProp, theme.dots.web.size.active]);
  var activeColor = React.useMemo(function () {
    return activeColorProp || dotsTheme.activeColor;
  }, [activeColorProp, dotsTheme.activeColor]);
  var inactiveColor = React.useMemo(function () {
    return inactiveColorProp || dotsTheme.inactiveColor;
  }, [inactiveColorProp, dotsTheme.inactiveColor]);
  var onLeftControlButtonClick = React.useCallback(function () {
    var newIndex = active <= 0 && carousel ? count - 1 : active - 1;
    // istanbul ignore next - if onChange is undefined, control buttons are not rendered
    onChange === null || onChange === undefined || onChange(newIndex);
    var message = (ariaLiveMessage === null || ariaLiveMessage === undefined ? undefined : ariaLiveMessage(newIndex, count)) || "Current step: ".concat(newIndex + 1);
    setAriaLiveAnnouncement(message);
  }, [active, carousel, count, onChange, ariaLiveMessage]);
  var onRightControlButtonClick = React.useCallback(function () {
    var newIndex = active >= count - 1 && carousel ? 0 : active + 1;
    // istanbul ignore next - if onChange is undefined, control buttons are not rendered
    onChange === null || onChange === undefined || onChange(newIndex);
    var message = (ariaLiveMessage === null || ariaLiveMessage === undefined ? undefined : ariaLiveMessage(newIndex, count)) || "Current step: ".concat(newIndex + 1);
    setAriaLiveAnnouncement(message);
  }, [active, carousel, count, onChange, ariaLiveMessage]);
  var createDots = React.useCallback(function () {
    return _toConsumableArray(Array(count).keys()).map(function (id) {
      var _getIsActive = getIsActive(progression, active, id),
        _getIsActive2 = _slicedToArray(_getIsActive, 2),
        isActive = _getIsActive2[0],
        isCurrent = _getIsActive2[1];
      return /*#__PURE__*/React.createElement(Dot, {
        key: "elemental-ui-Dot-".concat(id),
        id: id,
        ariaLabel: dotsAriaLabel,
        count: count,
        vertical: vertical,
        size: size,
        activeSize: activeSize,
        activeColor: activeColor,
        inactiveColor: inactiveColor,
        isActive: isActive,
        isCurrent: isCurrent,
        onChange: onChange,
        disabled: disabled
      });
    });
  }, [count, active, progression, size, activeSize, activeColor, inactiveColor, onChange, disabled, vertical, dotsAriaLabel]);
  return showControls ? /*#__PURE__*/React.createElement(Box, _extends({
    "data-testid": "elemental-ui-Dots",
    display: "flex",
    justifyContent: "center",
    flexDirection: "row",
    alignItems: "center",
    role: "group",
    "aria-live": "polite"
  }, props), /*#__PURE__*/React.createElement(IconButton, _extends({
    icon: /*#__PURE__*/React.createElement(IconChevronLeft, null),
    "data-testid": "elemental-ui-Dots-IconButton-previous-step",
    borderRadius: "100%",
    mr: "5px",
    status: "secondary",
    color: activeColor,
    disabled: disabled || !carousel && active <= 0,
    "aria-label": "Previous step",
    onClick: onLeftControlButtonClick
  }, leftControlButtonProps)), /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    flexDirection: "row",
    alignItems: "center"
  }, createDots()), /*#__PURE__*/React.createElement(IconButton, _extends({
    icon: /*#__PURE__*/React.createElement(IconChevronRight, null),
    "data-testid": "elemental-ui-Dots-IconButton-next-step",
    borderRadius: "100%",
    ml: "5px",
    status: "secondary",
    color: activeColor,
    disabled: disabled || !carousel && active >= count - 1,
    "aria-label": "Next step",
    onClick: onRightControlButtonClick
  }, rightControlButtonProps)), /*#__PURE__*/React.createElement(visuallyHidden, {
    style: {
      position: 'absolute',
      width: '1px',
      height: '1px',
      overflow: 'hidden',
      clip: 'rect(1px, 1px, 1px, 1px)',
      whiteSpace: 'nowrap'
    }
  }, ariaLiveAnnouncement)) : /*#__PURE__*/React.createElement(Box, _extends({
    role: onChange ? undefined : 'presentation',
    "data-testid": "elemental-ui-Dots",
    display: "flex",
    justifyContent: "center",
    flexDirection: vertical ? 'column' : 'row',
    alignItems: "center"
  }, props), createDots());
};

export { Dots };
